/**
 * Sound effects and music, made on the fly with Web Audio (no audio files to
 * download or license). Browsers only allow sound after the player has
 * clicked or tapped something, so nothing plays until then.
 *
 * Settings live in localStorage: they're a per-device preference.
 */

export type Sfx = 'coin' | 'levelUp' | 'splash' | 'harvest' | 'plant' | 'chime' | 'gift' | 'error' | 'click' | 'pick' | 'rockBreak' | 'snap';
export type MusicTheme = 'town' | 'beach' | 'home' | 'haunted' | 'none';

export interface SoundSettings {
  music: boolean;
  sfx: boolean;
  /** 0–1 */
  musicVolume: number;
  sfxVolume: number;
}

const KEY = 'bb_sound';
const DEFAULTS: SoundSettings = { music: true, sfx: true, musicVolume: 0.35, sfxVolume: 0.7 };

function loadSettings(): SoundSettings {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<SoundSettings>) };
  } catch {
    // Private mode or blocked storage: use the defaults.
  }
  return { ...DEFAULTS };
}

let settings = loadSettings();
const listeners = new Set<(s: SoundSettings) => void>();

export const getSoundSettings = () => settings;
export function setSoundSettings(patch: Partial<SoundSettings>) {
  settings = { ...settings, ...patch };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Not saved; still applies for this visit.
  }
  applyVolumes();
  if (!settings.music) stopMusic();
  else if (wantedTheme !== 'none') playMusic(wantedTheme);
  for (const l of listeners) l(settings);
}
export function onSoundSettings(fn: (s: SoundSettings) => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Audio graph: master → (music bus, sfx bus)
// ---------------------------------------------------------------------------

let ctx: AudioContext | null = null;
let musicBus: GainNode | null = null;
let sfxBus: GainNode | null = null;
let noise: AudioBuffer | null = null;

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  const master = ctx.createGain();
  master.connect(ctx.destination);
  musicBus = ctx.createGain();
  sfxBus = ctx.createGain();
  musicBus.connect(master);
  sfxBus.connect(master);
  applyVolumes();
  noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = noise.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return ctx;
}

function applyVolumes() {
  if (!ctx || !musicBus || !sfxBus) return;
  const t = ctx.currentTime;
  musicBus.gain.setTargetAtTime(settings.music ? settings.musicVolume * 0.5 : 0, t, 0.1);
  sfxBus.gain.setTargetAtTime(settings.sfx ? settings.sfxVolume : 0, t, 0.02);
}

let unlocked = false;
/** Call once at start-up: the first click or key press turns sound on. */
export function installSoundUnlock() {
  const unlock = () => {
    if (unlocked) return;
    const a = audio();
    if (!a) return;
    void a.resume().then(() => {
      unlocked = true;
      if (wantedTheme !== 'none' && settings.music) playMusic(wantedTheme);
    });
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

const hz = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);

function tone(at: number, midi: number, len: number, opts: { type?: OscillatorType; gain?: number; bus?: GainNode | null; slideTo?: number; attack?: number } = {}) {
  const a = ctx!;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = opts.type ?? 'triangle';
  o.frequency.setValueAtTime(hz(midi), at);
  if (opts.slideTo !== undefined) o.frequency.exponentialRampToValueAtTime(hz(opts.slideTo), at + len);
  const peak = opts.gain ?? 0.3;
  const attack = opts.attack ?? 0.01;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, at + len);
  o.connect(g).connect(opts.bus ?? sfxBus!);
  o.start(at);
  o.stop(at + len + 0.05);
}

function hiss(at: number, len: number, opts: { from: number; to: number; gain: number; bus?: GainNode | null; q?: number }) {
  const a = ctx!;
  const src = a.createBufferSource();
  src.buffer = noise;
  src.loop = true;
  const f = a.createBiquadFilter();
  f.type = 'bandpass';
  f.Q.value = opts.q ?? 1;
  f.frequency.setValueAtTime(opts.from, at);
  f.frequency.exponentialRampToValueAtTime(opts.to, at + len);
  const g = a.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(opts.gain, at + Math.min(0.08, len / 3));
  g.gain.exponentialRampToValueAtTime(0.0001, at + len);
  src.connect(f).connect(g).connect(opts.bus ?? sfxBus!);
  src.start(at);
  src.stop(at + len + 0.05);
}

/** Plays a sound effect (quietly does nothing before the first click, or when sounds are off). */
export function play(name: Sfx) {
  if (!settings.sfx || !unlocked || !audio()) return;
  const t = ctx!.currentTime + 0.01;
  switch (name) {
    case 'coin':
      tone(t, 83, 0.08, { type: 'square', gain: 0.12 });
      tone(t + 0.07, 88, 0.22, { type: 'square', gain: 0.12 });
      break;
    case 'levelUp':
      [72, 76, 79, 84].forEach((n, i) => tone(t + i * 0.09, n, 0.25, { type: 'triangle', gain: 0.28 }));
      tone(t + 0.36, 88, 0.6, { type: 'triangle', gain: 0.22 });
      break;
    case 'splash':
      hiss(t, 0.5, { from: 2400, to: 500, gain: 0.35 });
      tone(t, 60, 0.15, { type: 'sine', gain: 0.2, slideTo: 48 });
      break;
    case 'harvest':
      tone(t, 74, 0.09, { type: 'sine', gain: 0.3, slideTo: 81 });
      tone(t + 0.08, 81, 0.14, { type: 'sine', gain: 0.25, slideTo: 86 });
      break;
    case 'plant':
      hiss(t, 0.12, { from: 900, to: 300, gain: 0.25, q: 2 });
      tone(t + 0.04, 55, 0.12, { type: 'sine', gain: 0.25, slideTo: 50 });
      break;
    case 'chime':
      [79, 83, 86, 91].forEach((n, i) => tone(t + i * 0.07, n, 0.5, { type: 'sine', gain: 0.18 }));
      break;
    case 'gift':
      [84, 88, 91, 96, 91, 96].forEach((n, i) => tone(t + i * 0.06, n, 0.3, { type: 'sine', gain: 0.15 }));
      hiss(t, 0.35, { from: 6000, to: 9000, gain: 0.05, q: 4 });
      break;
    case 'error':
      tone(t, 62, 0.12, { type: 'square', gain: 0.08 });
      tone(t + 0.12, 58, 0.2, { type: 'square', gain: 0.08 });
      break;
    case 'click':
      tone(t, 96, 0.03, { type: 'sine', gain: 0.08 });
      break;
    // Deep Mine: a pickaxe striking rock, the rock giving way, and a pickaxe breaking.
    case 'pick':
      hiss(t, 0.06, { from: 3800, to: 1800, gain: 0.18, q: 3 });
      tone(t, 79, 0.05, { type: 'square', gain: 0.05, slideTo: 72 });
      break;
    case 'rockBreak':
      hiss(t, 0.35, { from: 1400, to: 160, gain: 0.4, q: 0.8 });
      tone(t, 45, 0.25, { type: 'sine', gain: 0.3, slideTo: 36 });
      break;
    case 'snap':
      hiss(t, 0.08, { from: 5000, to: 2500, gain: 0.3, q: 2 });
      tone(t + 0.02, 70, 0.3, { type: 'sawtooth', gain: 0.08, slideTo: 50 });
      break;
  }
}

// ---------------------------------------------------------------------------
// Music: a small looping sequencer per area
// ---------------------------------------------------------------------------

interface Song {
  bpm: number;
  /** MIDI notes per eighth note; null is a rest. */
  melody: (number | null)[];
  /** Root note per bar (8 eighths); a soft chord plays on each. */
  chords: number[][];
  bass: number[];
  lead: OscillatorType;
  waves?: boolean;
}

const SONGS: Record<Exclude<MusicTheme, 'none'>, Song> = {
  // Bouncy and bright: C major pentatonic.
  town: {
    bpm: 108,
    lead: 'triangle',
    melody: [72, null, 76, 79, 81, 79, 76, null, 74, 76, 72, null, 69, 72, 74, null, 76, null, 79, 81, 84, 81, 79, 76, 74, null, 72, 74, 72, null, null, null],
    chords: [[60, 64, 67], [57, 60, 64], [65, 69, 72], [67, 71, 74]],
    bass: [48, 45, 41, 43],
  },
  // Slow and dreamy, with waves.
  beach: {
    bpm: 76,
    lead: 'sine',
    waves: true,
    melody: [76, null, null, 74, 72, null, 69, null, 72, null, null, null, 74, null, 76, null, 79, null, null, 76, 74, null, 72, null, 74, null, null, null, null, null, null, null],
    chords: [[60, 64, 67, 71], [65, 69, 72, 76], [57, 60, 64, 67], [62, 65, 69, 72]],
    bass: [48, 41, 45, 50],
  },
  // A music box for the cottage and farm.
  home: {
    bpm: 92,
    lead: 'sine',
    melody: [84, 79, 76, 79, 84, 79, 76, 79, 83, 79, 74, 79, 83, 79, 74, 79, 81, 77, 72, 77, 81, 77, 72, 77, 79, 76, 72, 76, 79, null, null, null],
    chords: [[60, 64, 67], [55, 59, 62], [53, 57, 60], [55, 60, 64]],
    bass: [48, 43, 41, 43],
  },
  // Spooky but friendly: A minor with a creepy half-step.
  haunted: {
    bpm: 84,
    lead: 'square',
    melody: [69, null, 72, null, 71, 70, 69, null, 64, null, 65, null, 64, null, null, null, 69, null, 72, 76, 75, null, 72, null, 71, null, 68, null, 69, null, null, null],
    chords: [[57, 60, 64], [53, 57, 60], [52, 56, 59], [57, 60, 64]],
    bass: [45, 41, 40, 45],
  },
};

let wantedTheme: MusicTheme = 'none';
let playing: { theme: MusicTheme; timer: number; out: GainNode } | null = null;

function stopMusic() {
  if (!playing || !ctx) return;
  const { timer, out } = playing;
  window.clearInterval(timer);
  out.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.3);
  window.setTimeout(() => out.disconnect(), 1500);
  playing = null;
}

/** Switches the background music (fading between songs). 'none' stops it. */
export function playMusic(theme: MusicTheme) {
  wantedTheme = theme;
  if (playing?.theme === theme) return;
  stopMusic();
  if (theme === 'none' || !settings.music || !unlocked || !audio()) return;
  const song = SONGS[theme];
  const a = ctx!;
  const out = a.createGain();
  out.gain.setValueAtTime(0.0001, a.currentTime);
  out.gain.setTargetAtTime(1, a.currentTime, 0.8);
  out.connect(musicBus!);
  const eighth = 60 / song.bpm / 2;
  let step = 0;
  let next = a.currentTime + 0.1;
  // Schedules a little ahead so timers firing late don't make the music stutter.
  const tick = () => {
    // After a long pause (background tab), pick up from now instead of rushing through missed notes.
    if (next < a.currentTime - 0.1) next = a.currentTime + 0.05;
    while (next < a.currentTime + 0.25) {
      const i = step % song.melody.length;
      const bar = Math.floor(i / 8) % song.chords.length;
      const note = song.melody[i];
      if (note !== null && note !== undefined) tone(next, note, eighth * 1.6, { type: song.lead, gain: song.lead === 'square' ? 0.05 : 0.12, bus: out });
      if (i % 8 === 0) {
        for (const c of song.chords[bar]!) tone(next, c, eighth * 7.5, { type: 'sine', gain: 0.05, bus: out, attack: 0.15 });
        tone(next, song.bass[bar]!, eighth * 3.5, { type: 'triangle', gain: 0.14, bus: out });
        if (song.waves && bar % 2 === 0) hiss(next, eighth * 14, { from: 300, to: 900, gain: 0.04, bus: out, q: 0.6 });
      }
      if (i % 8 === 4) tone(next, song.bass[bar]! + 7, eighth * 3, { type: 'triangle', gain: 0.1, bus: out });
      step++;
      next += eighth;
    }
  };
  tick();
  playing = { theme, timer: window.setInterval(tick, 50), out };
}

// The tab going to the background pauses everything; coming back resumes it.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    if (document.hidden) void ctx.suspend();
    else if (unlocked) void ctx.resume();
  });
}
